import { useLiveData, useService } from '@toeverything/infra';
import { useEffect, useMemo, useState } from 'react';

import { AuthService } from '../services/auth';
import {
  type AvatarAssetCacheInput,
  AvatarAssetCacheService,
  createAvatarAssetObjectUrl,
  toAbsoluteAvatarAssetUrl,
} from '../services/avatar-asset-cache';
import { ServerService } from '../services/server';

export type CachedUserAvatarInput = {
  userId?: string | null;
  avatarUrl?: string | null;
  scopeAccountId?: string | null;
};

export function useCachedUserAvatarUrl({
  userId,
  avatarUrl,
  scopeAccountId,
}: CachedUserAvatarInput): string | null | undefined {
  const avatarCache = useService(AvatarAssetCacheService);
  const server = useService(ServerService).server;
  const authService = useService(AuthService);
  const currentAccount = useLiveData(authService.session.account$);
  const trimmedAvatarUrl = avatarUrl?.trim() || null;
  const accountId = scopeAccountId ?? currentAccount?.id ?? null;
  const sourceUrl = useMemo(() => {
    return trimmedAvatarUrl
      ? toAbsoluteAvatarAssetUrl(trimmedAvatarUrl, server.baseUrl)
      : null;
  }, [server.baseUrl, trimmedAvatarUrl]);
  const input = useMemo<AvatarAssetCacheInput | null>(() => {
    if (!sourceUrl || !accountId) {
      return null;
    }
    const entityId = userId ?? sourceUrl;

    return {
      kind: 'user-avatar',
      scope: {
        accountId,
        serverId: server.id,
      },
      entityId,
      source: sourceUrl,
    };
  }, [accountId, server.id, sourceUrl, userId]);
  const [cachedUrl, setCachedUrl] = useState<string | null>(null);

  useEffect(() => {
    let disposed = false;
    let currentObjectUrl: string | null = null;

    const setObjectUrl = (nextObjectUrl: string | null) => {
      if (currentObjectUrl) {
        URL.revokeObjectURL(currentObjectUrl);
      }
      currentObjectUrl = nextObjectUrl;
      if (!disposed) {
        setCachedUrl(nextObjectUrl);
      }
    };

    setCachedUrl(null);

    if (!input) {
      return () => {
        disposed = true;
      };
    }

    const showRecord = (
      record: Parameters<typeof createAvatarAssetObjectUrl>[0]
    ) => {
      const nextUrl = createAvatarAssetObjectUrl(record);
      if (disposed) {
        URL.revokeObjectURL(nextUrl);
        return;
      }
      setObjectUrl(nextUrl);
    };

    void (async () => {
      try {
        const cached = await avatarCache.get(input);
        if (cached) {
          showRecord(cached);
        }
      } catch {
        // Avatar caching is cosmetic; failures keep the live URL/fallback path.
      }

      try {
        const refreshed = await avatarCache.cacheUrl(input, server.baseUrl);
        if (refreshed) {
          showRecord(refreshed);
        }
      } catch {
        // External identity providers can block CORS. Keep the source URL fallback.
      }
    })();

    return () => {
      disposed = true;
      if (currentObjectUrl) {
        URL.revokeObjectURL(currentObjectUrl);
      }
    };
  }, [avatarCache, input, server.baseUrl]);

  return cachedUrl ?? trimmedAvatarUrl ?? avatarUrl;
}
